/**
 * plugins/index.ts
 * Automatically included in `./src/main.ts`
 */

// Plugins
import vuetify from './vuetify';
import { createPinia } from 'pinia';
import mdiVue from 'mdi-vue/v3';
import * as mdiIconsLib from '../plugins/mdiIcons';
// Types
import type { App } from 'vue';
import type { Router } from 'vue-router';
import { authviaRootStorePlugin } from '@authvia-v3/pinia-stores-root';
import { authviaUserStorePlugin } from '@authvia-v3/pinia-stores-user';
// directives
import customRipple from '../directives/customRipple';
// Action Packages - Now lazy loaded via actionPlugins.ts
// All action plugins are loaded on-demand when business process page is accessed
import { EnvironmentService } from '../services/EnvironmentService';
import { rebindLockSentryToApp } from '../utils/hostEmbed';
import { clarityPlugin } from './clarity';
// Action CSS files are lazy loaded via actionPlugins.ts (including ajv-schema-forms v2 for custom-form)
// @sentry/vue and vue-i18n are lazy loaded inside registerPlugins to avoid circular chunk init

const pinia = createPinia();

function hasLockSentry(): boolean {
  const dsn: string = EnvironmentService.getSentryDSN()?.trim() ?? '';
  return dsn.length > 0 && process.env.STORYBOOK !== 'true';
}

async function registerSentryPiniaPlugin(): Promise<void> {
  if (!hasLockSentry()) {
    return;
  }

  const Sentry = await import('@sentry/vue');
  pinia.use(Sentry.createSentryPiniaPlugin({}));
}

async function initLockSentry(app: App, appRouter: Router): Promise<void> {
  if (!hasLockSentry()) {
    return;
  }

  const Sentry = await import('@sentry/vue');
  if (Sentry.isInitialized()) {
    return;
  }

  const integration = Sentry.browserTracingIntegration({
    router: appRouter,
    routeLabel: 'path',
  });

  const sessionReplays = Sentry.replayIntegration({
    maskAllText: false,
    blockAllMedia: false,
    maskAllInputs: true,
  });

  const userFeedbacks = Sentry.feedbackIntegration({
    autoInject: true,
    colorScheme: 'system',
    isNameRequired: true,
    isEmailRequired: true,
    showBranding: false,
    showName: true,
    showEmail: true,
    enableScreenshot: false,
    formTitle: 'Send Feedback',
    triggerLabel: 'Send Feedback',
    buttonLabel: 'Feedback',
    submitButtonLabel: 'Send Feedback',
    messagePlaceholder: "What's the feedback? What did you expect?",
    successMessageText: 'We appreciate your feedback, thank you!',
  });

  const tracesSampleRate: number = import.meta.env.MODE === 'production' ? 0.2 : 0.8;
  Sentry.init({
    app,
    dsn: EnvironmentService.getSentryDSN(),
    release: EnvironmentService.getVersion(),
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    ignoreErrors: [
      /Script.*sw\.js.*load failed/i,
      /Invalid call to runtime\.sendMessage\(\)\. Tab not found\./i,
    ],
    integrations: [
      integration,
      sessionReplays,
      userFeedbacks,
      Sentry.httpClientIntegration(),
    ],
    environment: EnvironmentService.getNodeEnv(),
    tracesSampleRate: tracesSampleRate,
    tracePropagationTargets: [
      'localhost:8080',
      EnvironmentService.getAppURL(),
    ],
    normalizeDepth: 7,
  });
}

export async function registerPlugins(app: App) {
  const [
    { default: router },
    { initI18n },
    { AuthviaCustomerAuthenticationPlugin },
    { AuthviaPaymentMethodCreatePlugin },
    { AuthviaPaymentMethodSelectionPlugin },
  ] = await Promise.all([
    import('../router'),
    import('@/locales'),
    import('@authvia/customer-authentication'),
    import('@authvia/payment-method-create'),
    import('@authvia/payment-method-selection'),
  ]);
  const appRouter: Router = router;
  const i18n = await initI18n();

  await registerSentryPiniaPlugin();

  app
    .use(clarityPlugin)
    .use(pinia)
    .use(vuetify)
    .use(appRouter)
    .use(i18n)
    .use(authviaRootStorePlugin, {
      authviaAPI: EnvironmentService.getAPIHost(),
      authviaCDN: EnvironmentService.getCDNHost(),
    })
    .use(authviaUserStorePlugin, {
      authviaAPI: EnvironmentService.getAPIHost(),
      authviaCDN: EnvironmentService.getCDNHost(),
      // Default legacy; syncPiniaApiPathPrefix switches to '' for USER tokens.
      apiPathPrefix: '/v3',
    });

  // Lock Sentry must be ready before embed widgets install (they skip their own init and attach to the host client).
  await initLockSentry(app, appRouter);

  app
    .use(AuthviaCustomerAuthenticationPlugin, {
      apiBaseUrl: `https://${EnvironmentService.getLegacyAPIHost()}`,
    })
    .use(AuthviaPaymentMethodCreatePlugin, {
      apiBaseURL: EnvironmentService.getAPIHost(),
      cdnURL: EnvironmentService.getSecureCDNHost(),
      googleAPIKey: EnvironmentService.getGoogleAPIKey(),
      paypalClientId: EnvironmentService.getPaypalClientId(),
      paypalEnvironment: EnvironmentService.getPaypalEnvironment(),
      plaidEnv: EnvironmentService.getPlaidEnv(),
      isPlaidVisible: EnvironmentService.getPlaidIsVisible(),
      pazeSDKURL: EnvironmentService.getPazeSDKURL(),
      paypalSDKURL: EnvironmentService.getPaypalSDKURL(),
    })
    .use(AuthviaPaymentMethodSelectionPlugin, {
      apiBaseUrl: EnvironmentService.getSecureAPIHost(),
    })
    .use(mdiVue, {
      icons: mdiIconsLib,
    })
    .directive('custom-ripple', customRipple);

  await rebindLockSentryToApp(app);

  // #region Localization
  const i18nInstance = i18n.global;
  if (navigator.languages.length) {
    const baseLanguage: any = navigator.languages[0];
    (i18nInstance.locale as any).value = baseLanguage;
  } else {
    (i18nInstance.locale as any).value = navigator.language as any;
  }
  // #endregion
}
